import { useEffect, useState } from "react";

import { API_BASE_URL } from "../Services/ApiClient";
import { DEFAULT_SEO_CONFIG } from "./seoDefaults";
import { SeoPublicConfig } from "./types";

let cached: SeoPublicConfig | null = null;
let inflight: Promise<SeoPublicConfig> | null = null;

/**
 * Loads the public SEO payload once per page load.
 *
 * The request is made with plain fetch rather than the shared API client: this endpoint is public,
 * it has to work before anybody signs in, and it must not drag the token refresh logic into a
 * crawler visit. A failure is not fatal - the bundled defaults are used instead, which still carry
 * the right titles and still keep every route that is not in the registry out of the index.
 */
export function fetchSeoConfig(): Promise<SeoPublicConfig> {
  if (cached) {
    return Promise.resolve(cached);
  }
  if (inflight) {
    return inflight;
  }

  inflight = fetch(`${API_BASE_URL}/seo/config`, {
    headers: { Accept: "application/json" },
    credentials: "omit",
  })
    .then(async (response) => {
      if (!response.ok) {
        throw new Error(`SEO config request failed with status ${response.status}`);
      }
      const body = await response.json();
      const payload = body?.data ?? body;

      const config: SeoPublicConfig = {
        site: { ...DEFAULT_SEO_CONFIG.site, ...(payload?.site || {}) },
        pages: Array.isArray(payload?.pages) && payload.pages.length
          ? payload.pages
          : DEFAULT_SEO_CONFIG.pages,
      };

      cached = config;
      return config;
    })
    .catch(() => DEFAULT_SEO_CONFIG)
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/** Clears the cached payload. Used by the admin screen after a save so a refresh shows the edit. */
export function resetSeoConfigCache() {
  cached = null;
}

export function useSeoConfig(): SeoPublicConfig {
  const [config, setConfig] = useState<SeoPublicConfig>(DEFAULT_SEO_CONFIG);

  useEffect(() => {
    let active = true;

    fetchSeoConfig().then((next) => {
      if (active) {
        setConfig(next);
      }
    });

    return () => {
      active = false;
    };
  }, []);

  return config;
}
